import React, {useState, useEffect} from 'react';
import { PackageTrackingDetails, RequestChangeResponse, RequestChangePostResponse } from '../../../types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import FloatInputContainer from '../Shared/TextInput/FloatInputContainer';
import ActionIcon from '../Shared/Icons/ActionIcon';
import { InputRow, InputLabel, TextInputWrapper, SubmitButton, SpinnerContainer } from './RequestChangeInputs';
import toast from '../../../Scripts/App/displayToastMessage';


interface NeighbourAddress {
    ContactName: string;
    PhoneNumber: string;
    AddressLine1: string;
    AddressLine2: string;
    City: string;
    State: string;
    Postcode: string;
}

interface NeighbourData extends RequestChangeResponse {
    NeighbourAddress: NeighbourAddress;
}

interface DeliverToNeighbourProps {
    packageDetails: PackageTrackingDetails;
};

const DeliverToNeighbourComponent = ({packageDetails}: DeliverToNeighbourProps) => {
    const [t] = useTranslation();
    const [neighbourData, setNeighbourData] = useState({IsLoaded: false, Data: {} as NeighbourData});
    const [neighbourAddress, setNeighbourAddress] = useState({} as NeighbourAddress);
    const [validationErrors, setValidationErrors] = useState<{[key: string]: string[]} | undefined>();

    const loadNeighbourData = () => {
        const xhr = new XMLHttpRequest();
        const url = `Package/DeliverToNeighbourData` +
                    `?jobId=${packageDetails.JobId}` +
                    `&trackingNumber=${packageDetails.TrackingNumber}`;
        xhr.open('GET', url, true);
        xhr.onload = () => {
            const data = JSON.parse(xhr.responseText) as NeighbourData;
            setNeighbourData({IsLoaded: true, Data: data});
            if(data.Success)
            {
                setNeighbourAddress(data.NeighbourAddress);
            }
            if(data.Message) {
                toast(data.Message, data.MessageType ?? 'success');
            }
        };
        xhr.send();
    }

    const reloadNeighbourData = () => {
        setNeighbourData({IsLoaded: false, Data: {} as NeighbourData});
        loadNeighbourData();
    }

    const sendNeighbourRequest = () => {
        const xhr = new XMLHttpRequest();
        const url = 'Package/DeliverToNeighbour';
        xhr.open('POST', url);
        xhr.setRequestHeader('Content-type', 'application/json');
        const data = JSON.stringify({
            ...neighbourAddress,
            TrackingNumber: packageDetails.TrackingNumber, 
            JobId: packageDetails.JobId, 
            IsDelivery:  packageDetails.DeliveryOptions.IsDelivery,
        });
        xhr.onload = () => {
            const response = JSON.parse(xhr.responseText) as RequestChangePostResponse;
            
            if(response.Success)
            {
                // refresh the page
                const successMessage = packageDetails.DeliveryOptions.IsDelivery ? t('job_updated_deliver_to_neighbour') : t('job_updated_pickup_from_neighbour');
                window.location.href = `Package/Details?trackingNumber=${packageDetails.TrackingNumber}&message=${successMessage}`;
            }
            else
            {
                setValidationErrors(response.ValidationErrors);
                if(response.Message)
                    toast(response.Message, response.MessageType ?? 'success');
            }
        }
        xhr.send(data);
    }

    useEffect(() => {
        loadNeighbourData();
    }, []);


    if(!neighbourData.IsLoaded) //Loading
        return <SpinnerContainer />;

    else if(!neighbourData.Data.Success) //Load error
        return(
            <InputRow>
                <div>{t('request_change_load_error_message')}</div>
                <ActionIcon title={'refresh'} onClick={reloadNeighbourData}>refresh</ActionIcon>
            </InputRow>
        );

    return(
        <>
            <InputRow>
                <InputLabel>{t('address_contact_name')}</InputLabel>
                <TextInputWrapper>
                    <FloatInputContainer
                        value={neighbourAddress.ContactName ?? ''}
                        changeAction={(e) => setNeighbourAddress({...neighbourAddress, ContactName: e.currentTarget.value})}
                        showErrors={true}
                        isValid={!(validationErrors && validationErrors["ContactName"]?.length > 0)}
                        invalidMessages={validationErrors && validationErrors["ContactName"]}
                    />
                </TextInputWrapper>
            </InputRow>
            <InputRow>
                <InputLabel>{t('address_contact_number')}</InputLabel>
                <TextInputWrapper>
                    <FloatInputContainer
                        value={neighbourAddress.PhoneNumber ?? ''}
                        changeAction={(e) => setNeighbourAddress({...neighbourAddress, PhoneNumber: e.currentTarget.value})}
                        showErrors={true}
                        isValid={!(validationErrors && validationErrors["PhoneNumber"]?.length > 0)}
                        invalidMessages={validationErrors && validationErrors["PhoneNumber"]}
                    />
                </TextInputWrapper>
            </InputRow>
            <InputRow>
                <InputLabel>{t('address_address1_details')}</InputLabel>
                <TextInputWrapper>
                    <FloatInputContainer
                        value={neighbourAddress.AddressLine1 ?? ''}
                        changeAction={(e) => setNeighbourAddress({...neighbourAddress, AddressLine1: e.currentTarget.value})}
                        showErrors={true}
                        isValid={!(validationErrors && validationErrors["AddressLine1"]?.length > 0)}
                        invalidMessages={validationErrors && validationErrors["AddressLine1"]}
                    />
                </TextInputWrapper>
            </InputRow>
            <InputRow>
                <InputLabel />
                <TextInputWrapper>
                    <FloatInputContainer
                        value={neighbourAddress.AddressLine2 ?? ''}
                        changeAction={(e) => setNeighbourAddress({...neighbourAddress, AddressLine2: e.currentTarget.value})}
                        showErrors={true}
                        isValid={!(validationErrors && validationErrors["AddressLine2"]?.length > 0)}
                        invalidMessages={validationErrors && validationErrors["AddressLine2"]}
                    />
                </TextInputWrapper>
            </InputRow>
            <SubmitButton handleClick={sendNeighbourRequest} />
        </>
    );
};

export default DeliverToNeighbourComponent;